import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import {
  CloneQuoteData,
  PrepareQuoteDataForWorker,
  type QuoteData,
} from '@iqd/shared-quoting/core';
import { useSettings } from '../../shared/hooks/use-settings';
import { GENERATE_BUDGET_QUOTE, type GenerateBudgetQuoteResult } from './graphql';

function newSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export interface ContactDetails {
  email: string;
  phoneNumber: string;
}

export interface DomainContext {
  embeddingDomain: string;
  isEmbedded: boolean;
  referrerUrl: string;
}

export interface BudgetGenerationResult {
  drawing: unknown;
  backsheet: unknown;
  quoteData?: unknown;
  infoMessage?: string;
}

export interface UseBudgetSession {
  quoteData: QuoteData | null;
  drawing: unknown;
  backsheet: unknown;
  generating: boolean;
  setQuoteData: (qd: QuoteData) => void;
  reset: () => void;
  /**
   * Generates the budget quote with the customer's contact details attached in
   * the same call. Returns the result on success, or null if the API returned
   * nothing.
   */
  generateAndAttachContact: (
    quoteData: QuoteData,
    contact: ContactDetails,
    domain: DomainContext,
  ) => Promise<BudgetGenerationResult | null>;
}

export function useBudgetSession(): UseBudgetSession {
  const { settings } = useSettings();
  const [quoteData, setQuoteDataState] = useState<QuoteData | null>(null);
  const [drawing, setDrawing] = useState<unknown>(null);
  const [backsheet, setBacksheet] = useState<unknown>(null);
  const [sessionUuid, setSessionUuid] = useState<string>(() => newSessionId());

  const [generateMutation, { loading: generating }] =
    useMutation<GenerateBudgetQuoteResult>(GENERATE_BUDGET_QUOTE);

  const reset = useCallback(() => {
    if (!settings) return;
    const defaultQd = settings.get('budgetDefaultQd') as QuoteData | undefined;
    if (!defaultQd) {
      console.warn('budgetDefaultQd setting not present');
      return;
    }
    const fresh = CloneQuoteData(defaultQd);
    setQuoteDataState(fresh);
    setDrawing(null);
    setBacksheet(null);
    setSessionUuid(newSessionId());
  }, [settings]);

  useEffect(() => {
    if (settings && !quoteData) {
      reset();
    }
  }, [settings, quoteData, reset]);

  const setQuoteData = useCallback((qd: QuoteData) => setQuoteDataState(qd), []);

  const generateAndAttachContact = useCallback(
    async (
      qd: QuoteData,
      contact: ContactDetails,
      domain: DomainContext,
    ): Promise<BudgetGenerationResult | null> => {
      setQuoteDataState(qd);

      const generated = await generateMutation({
        variables: {
          sessionData: { username: 'guest', sessionId: sessionUuid },
          quoteData: PrepareQuoteDataForWorker(qd),
          contactDetails: {
            phoneNumber: contact.phoneNumber,
            emailAddress: contact.email,
            ...domain,
          },
        },
      });
      const payload = generated.data?.generateBudgetQuote;
      if (payload?.warnings && payload.warnings.length > 0) {
        throw new Error(payload.warnings.join('\n'));
      }
      if (payload?.errors && payload.errors.length > 0) {
        throw new Error(payload.errors.join('\n'));
      }
      if (!payload?.result) return null;

      setBacksheet(payload.result.backsheet);
      setDrawing(payload.result.drawing);
      if (payload.result.quoteData) {
        setQuoteDataState(payload.result.quoteData as QuoteData);
      }

      return payload.result;
    },
    [sessionUuid, generateMutation],
  );

  return useMemo(
    () => ({
      quoteData,
      drawing,
      backsheet,
      generating,
      setQuoteData,
      reset,
      generateAndAttachContact,
    }),
    [quoteData, drawing, backsheet, generating, setQuoteData, reset, generateAndAttachContact],
  );
}
